import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import {
  ActionMenu,
  Badge,
  Breaker,
  Button,
  Flexbox,
  InputField,
  StyledLink,
  WrapperBox,
} from "../../Theme/GlobalStyles";
import { Text } from "../../Theme/Typography";
import { globalTheme } from "../../Theme/Theme";
import {
  AddIcon,
  ArrowLeft,
  ArrowRight,
  OptionVerticle,
  Search,
  User,
} from "../../Theme/Icons";
import { ToolTip } from "../../Components/ToolTip/ToolTip";
import LogoutPopup from "../../Components/Popup/LogoutPopup";
import CreateUserPopup from "../../Components/Popup/CreateUserPopup";
import EditUserPopup from "../../Components/Popup/EditUserPopup";
import ManageRolesPopup from "../../Components/Popup/ManageRolesPopup";
import InitialsAvatar from "../../Components/InitialsAvatar";
import {
  getAllOrgUsers,
  createOrgUser,
  clearManagementSliceStates,
  getAllOrgRoles,
  deleteOrgUser,
  clearManagementSlicePermissionData,
  getAllOrgProgramsWithCohorts,
  clearManagementSliceData,
  updateCoworkUser,
  editOrgUserRole,
} from "../../../redux/userManagement/userManagingSlice";

// Roles a Class Admin may assign, and the role a target user must already hold
// for a Class Admin to manage them. Enforced again on the backend.
const CLASS_ROLE_NAMES = ["Cowork Class Admin", "Cowork Class User"];
const STAFF_ROLE_NAME = "Cowork Staff";

const PER_PAGE = 10;

const STATUS_OPTIONS = [
  { dropmenu: "All Statuses", value: "All" },
  { dropmenu: "Active", value: "Active" },
  { dropmenu: "Inactive", value: "Inactive" },
];

const capitalize = (str = "") =>
  str.replace(/\b\w/g, (char) => char.toUpperCase());

const SKELETON_BAR = {
  display: "block",
  height: 14,
  borderRadius: 6,
  background: "linear-gradient(90deg, #f0f0f0 25%, #e3e3e3 50%, #f0f0f0 75%)",
  backgroundSize: "200% 100%",
  animation: "shimmer 1.5s infinite",
};

// Shimmer row matching the 7 user columns (avatar + bars).
const UserSkeletonRow = () => (
  <tr>
    <td>
      <div className="tabBox">
        <span
          style={{ ...SKELETON_BAR, width: 28, height: 28, borderRadius: "50%" }}
        />
        <span style={{ ...SKELETON_BAR, width: 120 }} />
      </div>
    </td>
    {[180, 110, 70, 80, 90].map((w, i) => (
      <td key={i}>
        <div className="tabBox">
          <span style={{ ...SKELETON_BAR, width: w }} />
        </div>
      </td>
    ))}
    <td>
      <div className="tabBox">
        <span
          style={{ ...SKELETON_BAR, width: 28, height: 28, borderRadius: 12 }}
        />
      </div>
    </td>
  </tr>
);

const UserManagementDashboard = () => {
  const dispatch = useDispatch();

  const {
    isManagementSliceFetching,
    isManagementSliceSuccess,
    isManagementSliceError,
    managementSliceErrorMessage,
    managementSliceSuccessMessage,
    allOrgUsers,
    allOrgRoles,
    allOrgProgramsWithCohorts,
    userCount,
  } = useSelector((state) => state.management);

  const [isCreatePopup, setIsCreatePopup] = useState(false);
  const [isEditUserPopup, setIsEditUserPopup] = useState(false);
  const [isDeletePopup, setIsDeletePopup] = useState(false);
  const [isManageRolesPopup, setIsManageRolesPopup] = useState(false);
  const [selectedRoleUser, setSelectedRoleUser] = useState(null);

  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserName, setSelectedUserName] = useState(null);
  const [roleIds, setRoleIds] = useState([]);
  const [programIds, setProgramIds] = useState([]);

  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // Role (coworkRole id) and status are sent to the API, so they filter across
  // the whole result set — not just the page in hand.
  const [filters, setFilters] = useState({ role: "", status: "All" });

  const createUserFormObject = {
    firstName: "",
    lastName: "",
    uid: "",
    mobileNumber: "",
    email: "",
    alternateEmail: "",
    roles: [],
    programs: [],
  };
  const editUserFormObject = {
    firstName: "",
    lastName: "",
    uidNumber: "",
    mobileNumber: "",
    officialEmail: "",
  };

  const [createUserFormData, setCreateUserFormData] =
    useState(createUserFormObject);
  const [editUserFormData, setEditUserFormData] = useState(editUserFormObject);
  const [errors, setErrors] = useState({});

  const rolesCookie = Cookies.get("roles");
  const userRoles = rolesCookie ? JSON.parse(rolesCookie) : [];

  // Class roles are only assignable by a Class Admin.
  const coworkRoles = useMemo(
    () =>
      (allOrgRoles || []).filter(
        (role) => !role.roleName?.toLowerCase()?.includes("class")
      ),
    [allOrgRoles]
  );

  const isCoworkAdmin = userRoles.includes("Cowork Admin");
  const isClassAdmin = userRoles.includes("Cowork Class Admin");

  // Roles the current admin may assign via "Manage Roles". A Cowork Admin can
  // assign anything; a Class Admin is limited to the two class roles.
  const assignableRoles = useMemo(() => {
    if (isCoworkAdmin) return allOrgRoles || [];
    if (isClassAdmin) {
      return (allOrgRoles || []).filter((role) =>
        CLASS_ROLE_NAMES.includes(role.roleName)
      );
    }
    return [];
  }, [allOrgRoles, isCoworkAdmin, isClassAdmin]);

  // A Class Admin may only manage staff users; a Cowork Admin, anyone.
  const canManageRoles = (user) => {
    if (isCoworkAdmin) return true;
    if (isClassAdmin) {
      return (user?.coworkRole || []).some(
        (r) => r?.label === STAFF_ROLE_NAME
      );
    }
    return false;
  };

  const roleFilterOptions = useMemo(() => {
    const list = (allOrgRoles || []).map((role) => ({
      dropmenu: role?.roleName,
      value: role?.id,
    }));
    return [{ dropmenu: "All Roles", value: "" }, ...list];
  }, [allOrgRoles]);

  // Debounced search — also resets pagination so we don't land on an empty page.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput.trim());
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Lookup data (roles + programs) for the create-user dropdowns and role filter.
  useEffect(() => {
    dispatch(getAllOrgRoles());
    dispatch(getAllOrgProgramsWithCohorts());
    return () => {
      dispatch(clearManagementSliceStates());
      dispatch(clearManagementSliceData());
    };
  }, [dispatch]);

  // Fetch users on search / filter / page change. The slice APPENDS on
  // fulfilled, so the list has to be cleared before each fetch.
  useEffect(() => {
    dispatch(clearManagementSliceData());
    dispatch(
      getAllOrgUsers({
        perPageCount: PER_PAGE,
        pageNumber: currentPage,
        searchValue: searchQuery,
        status: filters.status,
        roleId: filters.role,
      })
    );
  }, [dispatch, searchQuery, currentPage, filters]);

  // Success — close popups, toast, and refresh the page in view.
  useEffect(() => {
    if (!isManagementSliceSuccess) return;
    setIsCreatePopup(false);
    setIsEditUserPopup(false);
    setIsDeletePopup(false);
    setIsManageRolesPopup(false);
    toast.success(managementSliceSuccessMessage, {
      autoClose: 3000,
    });
    dispatch(clearManagementSliceStates());
    dispatch(clearManagementSlicePermissionData());
    dispatch(clearManagementSliceData());
    dispatch(
      getAllOrgUsers({
        perPageCount: PER_PAGE,
        pageNumber: currentPage,
        searchValue: searchQuery,
        status: filters.status,
        roleId: filters.role,
      })
    );
  }, [isManagementSliceSuccess]);

  useEffect(() => {
    if (!isManagementSliceError) return;
    toast.error(managementSliceErrorMessage, {
      autoClose: 3000,
    });
    dispatch(clearManagementSliceStates());
    dispatch(clearManagementSlicePermissionData());
  }, [isManagementSliceError]);

  // Changing a filter re-queries from page 1 so we don't land past the last page.
  const updateFilter = (patch) => {
    setCurrentPage(1);
    setFilters((prev) => ({ ...prev, ...patch }));
  };

  const visibleUsers = allOrgUsers || [];

  const totalPages = Math.max(1, Math.ceil((userCount || 0) / PER_PAGE));

  // Windowed page list: first/last page always shown, current page with one
  // neighbour on each side, "…" where pages are skipped.
  const pageItems = useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const items = [1];
    if (currentPage > 3) items.push("…");
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    for (let i = start; i <= end; i++) items.push(i);
    if (currentPage < totalPages - 2) items.push("…");
    items.push(totalPages);
    return items;
  }, [currentPage, totalPages]);

  const onChangeCreateFormData = (event) => {
    const { name, value } = event.target;
    setCreateUserFormData((prev) => ({ ...prev, [name]: value?.trim() }));
    setErrors({});
  };

  const onChangeEditFormData = (event) => {
    const { name, value } = event.target;
    setEditUserFormData((prev) => ({ ...prev, [name]: value?.trim() }));
    setErrors({});
  };

  const openCreatePopup = () => {
    setCreateUserFormData(createUserFormObject);
    setRoleIds([]);
    setProgramIds([]);
    setErrors({});
    setIsCreatePopup(true);
  };

  const openEditUserPopup = (user) => {
    setSelectedUserId(user?.id);
    setEditUserFormData({
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      uidNumber: user?.uidNumber || "",
      mobileNumber: user?.mobileNumber || "",
      officialEmail: user?.officialEmail || "",
    });
    setErrors({});
    setIsEditUserPopup(true);
  };

  const openDeletePopup = (user) => {
    setSelectedUserId(user?.id);
    setSelectedUserName(
      capitalize(`${user?.firstName || ""} ${user?.lastName || ""}`.trim()) ||
        "this user"
    );
    setIsDeletePopup(true);
  };

  const openManageRolesPopup = (user) => {
    setSelectedRoleUser(user);
    setIsManageRolesPopup(true);
  };

  const saveUserRoles = (selectedRoles) => {
    if (!selectedRoleUser?.id) return;
    dispatch(
      editOrgUserRole({
        userId: selectedRoleUser.id,
        roles: (selectedRoles || []).map((r) => ({
          value: r.value,
          label: r.label,
        })),
      })
    );
  };

  const createNewUser = () => {
    const newErrors = {};
    if (!createUserFormData.firstName) {
      newErrors.firstName = "First name is required.";
    }
    if (!createUserFormData.email) {
      newErrors.email = "Email is required.";
    } else if (!/\S+@\S+/.test(createUserFormData.email)) {
      newErrors.email = "Invalid email address.";
    }
    if (!createUserFormData.mobileNumber) {
      newErrors.mobileNumber = "Phone Number is required.";
    } else if (!/^[1-9]\d{9}$/.test(createUserFormData.mobileNumber)) {
      newErrors.mobileNumber = "Invalid mobile number.";
    }
    if (newErrors.firstName || newErrors.email || newErrors.mobileNumber) {
      setErrors(newErrors);
      return;
    }

    const userObj = { ...createUserFormData };
    userObj.roles = roleIds;
    userObj.programs = (programIds || []).flatMap((data) =>
      (allOrgProgramsWithCohorts || []).filter(
        (item) => item.batchId === data.value
      )
    );
    dispatch(createOrgUser(userObj));
  };

  const updateUser = () => {
    const newErrors = {};
    if (!editUserFormData.mobileNumber) {
      newErrors.mobileNumber = "Phone Number is required.";
    } else if (!/^[1-9]\d{9}$/.test(editUserFormData.mobileNumber)) {
      newErrors.mobileNumber = "Invalid mobile number.";
    }
    if (
      editUserFormData.officialEmail &&
      !/\S+@\S+/.test(editUserFormData.officialEmail)
    ) {
      newErrors.officialEmail = "Invalid email address.";
    }
    if (newErrors.mobileNumber || newErrors.officialEmail) {
      setErrors(newErrors);
      return;
    }

    // Only send the fields the admin actually filled in.
    const updateData = {};
    Object.keys(editUserFormData).forEach((key) => {
      if (editUserFormData[key] !== undefined && editUserFormData[key] !== "") {
        updateData[key] = editUserFormData[key];
      }
    });
    dispatch(updateCoworkUser({ userId: selectedUserId, updateData }));
  };

  const deleteUser = (userId) => {
    dispatch(deleteOrgUser({ userId }));
  };

  return (
    <WrapperBox background={"transparent"}>

      <CreateUserPopup
        isActive={isCreatePopup}
        onClickCreateUser={createNewUser}
        onClickClosePopup={() => setIsCreatePopup(false)}
        onChangeFormData={onChangeCreateFormData}
        errors={errors}
        isCreateUserPopupFetching={isManagementSliceFetching}
        dropdownOptions={
          userRoles.includes("Cowork Class Admin") ? allOrgRoles : coworkRoles
        }
        programList={allOrgProgramsWithCohorts}
        setRoleIds={setRoleIds}
        setProgramIds={setProgramIds}
      />

      <EditUserPopup
        isActive={isEditUserPopup}
        onClickUpdateUser={updateUser}
        onClickClosePopup={() => setIsEditUserPopup(false)}
        onChangeFormData={onChangeEditFormData}
        errors={errors}
        isEditUserPopupFetching={isManagementSliceFetching}
        formData={editUserFormData}
      />

      <ManageRolesPopup
        isActive={isManageRolesPopup}
        user={selectedRoleUser}
        assignableRoles={assignableRoles}
        isRestricted={isClassAdmin && !isCoworkAdmin}
        onClose={() => setIsManageRolesPopup(false)}
        onSubmit={saveUserRoles}
        isFetching={isManagementSliceFetching}
      />

      <LogoutPopup
        showActive={isDeletePopup}
        message={`Are you sure you want to delete ${
          selectedUserName || "this user"
        }?`}
        onCancel={setIsDeletePopup}
        onAllow={deleteUser}
        userId={selectedUserId}
        isPopupFetching={isManagementSliceFetching}
      />

      <WrapperBox
        borderColor={globalTheme.colors.nuetral2}
        borderRadius={20}
        padding={"0"}
        boxShadow
      >
        {/* Header — title, search, filters, create */}
        <Flexbox spaceBW alignCenter padding="20px" gap={12}>
          <Flexbox maxWidth="max-content" gap={12} alignCenter>
            <Text font16 inter medium black4>
              All Users
            </Text>
            <Badge
              statusText={`${userCount || 0} total`}
              primaryGrey
              small
              borderRadius={"20px"}
            />
          </Flexbox>

          <Flexbox maxWidth="75%" gap={12} alignCenter flexEnd>
            <InputField
              noLabel
              hasIcon={<Search />}
              height="40px"
              placeholder="Search users ..."
              revIcon
              maxWidth="300px"
              minWidth="250px"
              autoFocus
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.preventDefault();
              }}
            />
            {
              isCoworkAdmin && 
              <WrapperBox
                background={globalTheme.colors.nuetral2}
                maxWidth="max-content"
                padding={"12px"}
                borderRadius={50}
                borderColor={globalTheme.colors.nuetral2}
                maxHeight={"max-content"}
                className={"pointer"}
                overflow={"visible"}
              >
                <ActionMenu
                  btnName="All Roles"
                  menuList={roleFilterOptions}
                  selectedValue={filters.role}
                  onSelect={(value) => updateFilter({ role: value })}
                />
              </WrapperBox>
            }

            <WrapperBox
              background={globalTheme.colors.nuetral2}
              maxWidth="max-content"
              padding={"12px"}
              borderRadius={50}
              borderColor={globalTheme.colors.nuetral2}
              maxHeight={"max-content"}
              className={"pointer"}
              overflow={"visible"}
            >
              <ActionMenu
                btnName="All Statuses"
                menuList={STATUS_OPTIONS}
                selectedValue={filters.status}
                onSelect={(value) => updateFilter({ status: value })}
              />
            </WrapperBox>
            {
              isCoworkAdmin && 
              <Button
                name="Create User"
                primaryFilled
                buttonMd
                icon={<AddIcon />}
                revIcon
                onClick={openCreatePopup}
              />
            }
          </Flexbox>
        </Flexbox>

        {/* Table */}
        <div className="tableWrapper">
          <div className="mainTable">
            <table>
              <thead>
                <tr>
                  <th>
                    <div className="tabBox">Name</div>
                  </th>
                  <th>
                    <div className="tabBox">Email ID</div>
                  </th>
                  <th>
                    <div className="tabBox">Mobile Number</div>
                  </th>
                  <th>
                    <div className="tabBox">UID</div>
                  </th>
                  <th>
                    <div className="tabBox">User Status</div>
                  </th>
                  <th>
                    <div className="tabBox">Role</div>
                  </th>
                  <th>
                    <div className="tabBox">Action</div>
                  </th>
                </tr>
              </thead>

              <tbody>
                {visibleUsers.length === 0 ? (
                  isManagementSliceFetching ? (
                    [...Array(6)].map((_, i) => <UserSkeletonRow key={i} />)
                  ) : (
                    <tr>
                      <td colSpan={7}>
                        <Flexbox
                          justiCenter
                          alignCenter
                          style={{ padding: "40px 0" }}
                        >
                          <Text font14 inter regular grey2>
                            No users found
                          </Text>
                        </Flexbox>
                      </td>
                    </tr>
                  )
                ) : (
                  visibleUsers.map((orgUser) => {
                    const roles = orgUser?.coworkRole || [];
                    const isActive = orgUser?.userStatus === "Active";
                    return (
                      <tr key={orgUser?.id}>
                        <td>
                          <div className="tabBox">
                            <InitialsAvatar
                              firstName={orgUser?.firstName}
                              lastName={orgUser?.lastName}
                            />
                            <StyledLink
                              color={globalTheme.colors.black4}
                              fontWeights="500"
                            >
                              {capitalize(
                                `${orgUser?.firstName || ""} ${
                                  orgUser?.lastName || ""
                                }`.trim()
                              ) || "—"}
                            </StyledLink>
                          </div>
                        </td>
                        <td>
                          <div className="tabBox">
                            {orgUser?.officialEmail || "—"}
                          </div>
                        </td>
                        <td>
                          <div className="tabBox">
                            {orgUser?.mobileNumber || "—"}
                          </div>
                        </td>
                        <td>
                          <div className="tabBox">
                            {orgUser?.uidNumber || "—"}
                          </div>
                        </td>
                        <td>
                          <div className="tabBox">
                            <Badge
                              statusText={orgUser?.userStatus || "—"}
                              green={isActive}
                              red={!isActive}
                              small
                              borderRadius={"20px"}
                            />
                          </div>
                        </td>
                        <td>
                          <div className="tabBox">
                            {roles.length > 0 ? (
                              <Flexbox
                                gap={6}
                                alignCenter
                                maxWidth="max-content"
                              >
                                <Flexbox
                                  gap={4}
                                  alignCenter
                                  maxWidth="max-content"
                                >
                                  <User color={globalTheme.colors.nuetral6} />
                                  <Text font13 inter medium nuetral13>
                                    {roles[0]?.label}
                                  </Text>
                                </Flexbox>
                                {roles.length > 1 && (
                                  <ToolTip
                                    position="Bottom"
                                    minWidth="max-content"
                                    content={
                                      <Flexbox
                                        column
                                        gap={10}
                                        alignStart
                                        style={{ padding: "2px 4px" }}
                                      >
                                        {roles.map((r, i) => (
                                          <Text
                                            key={r?.value || r?.label || i}
                                            font13
                                            inter
                                            medium
                                            white
                                            style={{ whiteSpace: "nowrap" }}
                                          >
                                            {r?.label}
                                          </Text>
                                        ))}
                                      </Flexbox>
                                    }
                                  >
                                    <Badge
                                      statusText={`+${roles.length - 1} more`}
                                      blue
                                      small
                                      borderRadius={"20px"}
                                    />
                                  </ToolTip>
                                )}
                              </Flexbox>
                            ) : (
                              "—"
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="tabBox">
                            <ActionMenu
                              staticLabel
                              btnName=""
                              toggleIcon={<OptionVerticle />}
                              menuList={[
                                ...(isClassAdmin
                                  ? [
                                      {
                                        dropmenu: "Manage Roles",
                                        action: () =>
                                          openManageRolesPopup(orgUser),
                                      },
                                    ]
                                  : [
                                    {
                                    dropmenu: "Edit User",
                                    action: () => openEditUserPopup(orgUser),
                                    },
                                    {
                                      dropmenu: "Delete User",
                                      action: () => openDeletePopup(orgUser),
                                    },
                                  ]),
                              ]}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <Breaker borderColor={globalTheme.colors.nuetral2} />
        <Flexbox spaceBW alignCenter padding="20px">
          <Button
            name="Previous"
            primaryOutlined
            buttonMd
            icon={<ArrowLeft />}
            revIcon
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          />
          <Flexbox gap={4} alignCenter justiCenter maxWidth="max-content">
            {pageItems.map((item, idx) =>
              item === "…" ? (
                <div
                  key={`gap-${idx}`}
                  style={{
                    width: 36,
                    height: 36,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Text font14 inter regular nuetral6>
                    …
                  </Text>
                </div>
              ) : (
                <div
                  key={item}
                  onClick={() => setCurrentPage(item)}
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    background:
                      item === currentPage
                        ? globalTheme.colors.nuetral2
                        : "transparent",
                  }}
                >
                  <Text
                    font14
                    inter
                    {...(item === currentPage
                      ? { medium: true, black4: true }
                      : { regular: true, nuetral6: true })}
                  >
                    {item}
                  </Text>
                </div>
              )
            )}
          </Flexbox>
          <Button
            name="Next"
            primaryOutlined
            buttonMd
            icon={<ArrowRight />}
            disabled={currentPage >= totalPages}
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          />
        </Flexbox>
      </WrapperBox>
    </WrapperBox>
  );
};

export default UserManagementDashboard;
